'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  groupByLength,
  loadWordIndex,
  solveAnagrams,
  unscramble,
  type WordIndex,
} from '@/lib/browser-word-tools';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type WordUnscramblerVariant = 'word-unscrambler' | 'anagram-solver';

export default function BrowserWordUnscrambler({ variant }: { variant: WordUnscramblerVariant }) {
  const isAnagram = variant === 'anagram-solver';
  const usageReported = useRef(false);

  const [index, setIndex] = useState<WordIndex | null>(null);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [letters, setLetters] = useState(isAnagram ? 'listen' : 'craeton');
  const [mustContain, setMustContain] = useState('');
  const [minLength, setMinLength] = useState(3);

  useEffect(() => {
    setLoadState('loading');
    loadWordIndex()
      .then((loaded) => {
        setIndex(loaded);
        setLoadState('ready');
      })
      .catch(() => setLoadState('error'));
  }, []);

  // Usage stats once per session, outside render per react-hooks/refs.
  useEffect(() => {
    if (!index || usageReported.current) return;
    if (letters.replace(/[^a-z]/gi, '').length >= 2) {
      usageReported.current = true;
      void consumeToolUsage(variant, '').catch(() => undefined);
    }
  }, [index, letters, variant]);

  const results = useMemo(() => {
    if (!index) return null;
    if (isAnagram) {
      const exact = solveAnagrams(index, letters);
      const partial = unscramble(index, letters, { minLength: 3, limit: 200 }).filter(
        (word) => !exact.includes(word) && word.length < letters.replace(/[^a-z]/gi, '').length,
      );
      return { exact, groups: groupByLength(partial) };
    }
    const words = unscramble(index, letters, { mustContain, minLength, limit: 500 });
    return { exact: [] as string[], groups: groupByLength(words) };
  }, [index, letters, mustContain, minLength, isAnagram, variant]);

  const total = results
    ? results.exact.length + results.groups.reduce((s, g) => s + g.words.length, 0)
    : 0;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{isAnagram ? 'Solve the anagram' : 'Unscramble your letters'}</h2>
          <p>
            {isAnagram
              ? 'Type a word and get every exact anagram in a 168,000-word public-domain list, plus the shorter words hiding inside.'
              : 'Type your letters and get every word they can spell, longest first, from a 168,000-word public-domain list. Everything runs in your browser.'}
          </p>
        </div>
        <span className="tool-live-badge">{isAnagram ? 'Anagram' : 'Words'}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ gap: '0.75rem' }}>
          {loadState === 'loading' ? (
            <p className="tool-live-panel-note">
              Loading the word list, about 0.4 MB, cached after the first visit…
            </p>
          ) : null}
          {loadState === 'error' ? (
            <p className="support-feedback support-feedback-error">
              The word list could not be loaded. Check your connection and reload the page.
            </p>
          ) : null}

          {results ? (
            <div style={{ width: '100%' }}>
              {isAnagram ? (
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>
                    {results.exact.length
                      ? `Exact anagrams (${results.exact.length}): `
                      : 'No exact anagram in the list. '}
                  </strong>
                  <span style={{ fontSize: '1.05rem' }}>{results.exact.join(', ')}</span>
                </div>
              ) : null}
              {results.groups.map((group) => (
                <div key={group.length} style={{ marginBottom: '0.6rem' }}>
                  <small style={{ opacity: 0.7 }}>
                    {group.length} letters ({group.words.length})
                  </small>
                  <div style={{ lineHeight: 1.9 }}>
                    {group.words.map((word) => (
                      <code
                        key={word}
                        style={{
                          background: 'var(--surface-2, #1c2230)',
                          borderRadius: 6,
                          display: 'inline-block',
                          margin: '0 0.3rem 0.3rem 0',
                          padding: '0.15rem 0.5rem',
                        }}
                      >
                        {word}
                      </code>
                    ))}
                  </div>
                </div>
              ))}
              {total === 0 && loadState === 'ready' ? (
                <p className="tool-live-panel-note">
                  Nothing found. Check the letters, or relax the filters.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <label>
            {isAnagram ? 'Your word' : 'Your letters'}
            <input
              maxLength={15}
              onChange={(event) => setLetters(event.target.value)}
              spellCheck={false}
              type="text"
              value={letters}
            />
            <small>
              {isAnagram ? 'Up to 15 letters' : 'Up to 15 letters, order does not matter'}
            </small>
          </label>

          {!isAnagram ? (
            <>
              <label>
                Must contain (optional)
                <input
                  maxLength={5}
                  onChange={(event) => setMustContain(event.target.value)}
                  spellCheck={false}
                  type="text"
                  value={mustContain}
                />
              </label>
              <label>
                Minimum length
                <select
                  onChange={(event) => setMinLength(Number(event.target.value))}
                  value={minLength}
                >
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n} letters
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}

          {loadState === 'ready' && index ? (
            <div className="tool-live-stats">
              <div>
                <strong>{total}</strong>
                <span>words found in {index.wordCount.toLocaleString('en-US')} entries</span>
              </div>
            </div>
          ) : null}

          <p className="tool-live-panel-note">
            The dictionary is the public-domain ENABLE word list. It is large and serious, but it is
            not the official dictionary of any board game, and very new words and proper nouns are
            absent.
          </p>
        </aside>
      </div>
    </article>
  );
}
